/**
 * Product helper functions for Magento ProductInterface
 *
 * Pure utility functions for extracting data from standard Magento products.
 * Moved from types/products.ts to follow separation of concerns.
 */

import type { MagentoProduct } from '../types'

/**
 * Get the full product URL
 * Priority: canonical_url > url_key + url_suffix
 */
export function getMagentoProductUrl(product: MagentoProduct): string {
  // Use canonical_url if available (full URL from Magento)
  if (product.canonical_url) {
    return product.canonical_url
  }

  // Build URL from url_key + url_suffix
  const suffix = product.url_suffix ?? '.html'
  return `/${product.url_key}${suffix}`
}

/**
 * Get the best available image URL for a product
 * Priority: small_image > image > first media_gallery item
 */
export function getMagentoProductImageUrl(product: MagentoProduct): string | null {
  return (
    product.small_image?.url ??
    product.image?.url ??
    product.media_gallery[0]?.url ??
    null
  )
}

/**
 * Get hover image URL (second image from media gallery)
 */
export function getMagentoProductHoverImageUrl(product: MagentoProduct): string | null {
  return product.media_gallery[1]?.url ?? null
}

/**
 * Check if product is simple type (for add to cart button)
 */
export function isMagentoSimpleProduct(product: MagentoProduct): boolean {
  return product.type_id === 'simple'
}

/**
 * Get final price value
 */
export function getMagentoFinalPrice(product: MagentoProduct): number {
  return product.price_range.minimum_price.final_price.value
}

/**
 * Get regular price value
 */
export function getMagentoRegularPrice(product: MagentoProduct): number {
  return product.price_range.minimum_price.regular_price.value
}

/**
 * Get discount amount
 */
export function getMagentoDiscountAmount(product: MagentoProduct): number | null {
  return product.price_range.minimum_price.discount?.amount_off ?? null
}

/**
 * Get custom attribute value by code
 */
export function getMagentoCustomAttribute(
  product: MagentoProduct,
  attributeCode: string
): string | null {
  const attribute = product.custom_attributes?.find(
    (attr) => attr.attribute_code === attributeCode
  )
  return attribute?.value ?? null
}

/**
 * Get MSRP value from custom attributes
 */
export function getMagentoMsrp(product: MagentoProduct): number | null {
  const msrpValue = getMagentoCustomAttribute(product, 'msrp')
  if (!msrpValue) return null
  const parsed = parseFloat(msrpValue)
  return isNaN(parsed) || parsed <= 0 ? null : parsed
}

/**
 * Check if product is marked as new (within new_from_date..new_to_date range)
 */
export function isMagentoProductNew(product: MagentoProduct): boolean {
  const { new_from_date, new_to_date } = product
  if (!new_from_date) return false
  const now = new Date()
  const from = new Date(new_from_date)
  if (now < from) return false
  if (new_to_date) {
    const to = new Date(new_to_date)
    if (now > to) return false
  }
  return true
}

/**
 * Check if product has video entries in media gallery
 */
export function hasMagentoProductVideo(product: MagentoProduct): boolean {
  return product.media_gallery.some((item) => !!item.video_content?.video_url)
}
